<template>
  <div class="tiempos-prep">
    <div class="tiempos-prep__header">
      <h2 class="tiempos-prep__titulo">
        Tiempos de preparación
        <span class="tiempos-prep__rango" v-if="rango">
          Del {{ fmtFecha(rango.desde) }} al {{ fmtFecha(rango.hasta) }}
        </span>
      </h2>
      <SelectorPeriodo :dias="7" @cambio="alCambiarPeriodo" />
    </div>

    <el-alert
      v-if="noDisponible"
      title="Tiempos no disponibles en este momento"
      description="La fuente de datos no está respondiendo. Volvé a intentar en unos minutos."
      type="info"
      :closable="false"
      show-icon
      class="mb-3"
    ></el-alert>

    <div v-else-if="cargando" class="tiempos-prep__cargando">Cargando pedidos…</div>

    <template v-else>
      <div class="tiempos-prep__toolbar">
        <base-input
          v-model="busqueda"
          prepend-icon="fas fa-search"
          placeholder="Buscar por N° de pedido, tipo o estado..."
          class="tiempos-prep__buscar"
        ></base-input>
        <base-button type="success" size="sm" @click="descargar" :disabled="!pedidosFiltrados.length">
          <i class="fas fa-download"></i> Descargar Excel
        </base-button>
      </div>

      <el-table :data="pedidosPaginados" size="small" stripe empty-text="Sin pedidos en el periodo">
        <el-table-column prop="pedido_id" label="Pedido" width="110" align="center" header-align="center">
          <template slot-scope="s">#{{ s.row.pedido_id }}</template>
        </el-table-column>
        <el-table-column prop="fecha" label="Fecha" width="120" align="center" header-align="center"></el-table-column>
        <el-table-column label="Inicio preparación" width="160" align="center" header-align="center">
          <template slot-scope="s">{{ fmtHora(s.row.inicio_preparacion) }}</template>
        </el-table-column>
        <el-table-column label="Listo" width="120" align="center" header-align="center">
          <template slot-scope="s">{{ fmtHora(s.row.listo) }}</template>
        </el-table-column>
        <el-table-column label="Preparación" width="130" align="center" header-align="center">
          <template slot-scope="s">
            <span :class="{ 'celda-na': s.row.prep_min === null }">
              {{ s.row.prep_min === null ? 'N/A' : s.row.prep_min.toFixed(1) + ' min' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Tipo" width="110" align="center" header-align="center">
          <template slot-scope="s">{{ s.row.tipo_entrega === 'pickup' ? 'Recojo' : 'Delivery' }}</template>
        </el-table-column>
        <el-table-column label="" min-width="140">
          <template slot-scope="s">
            <span v-if="s.row.cancelado" class="pill pill--roja">Cancelado</span>
            <span v-else-if="s.row.programado" class="pill pill--neutra">Programado</span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-if="pedidosFiltrados.length > porPagina"
        class="tiempos-prep__paginacion"
        layout="total, prev, pager, next"
        :total="pedidosFiltrados.length"
        :page-size="porPagina"
        :current-page.sync="paginaActual"
      ></el-pagination>

    </template>
  </div>
</template>

<script>
import { Alert, Table, TableColumn, Pagination } from 'element-ui'
import SelectorPeriodo from '@/components/Metrics/SelectorPeriodo.vue'
import MetricasCocinaGateway from '@/gateways/MetricasCocinaGateway'
import { descargarExcel } from '@/utils/excel'

/**
 * Pedidos individuales de la cocina del catering autenticado, con su tiempo
 * de preparacion. Complementa la card "Tiempo de preparación" de Metricas de
 * cocina: la card da el promedio, esta lista muestra QUE pedidos fueron
 * lentos. Nunca envia una cocina: el backend la resuelve del token.
 */
export default {
  name: 'TiemposPreparacion',
  components: {
    SelectorPeriodo,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Alert.name]: Alert,
    [Pagination.name]: Pagination
  },
  data () {
    return {
      // Los pone SelectorPeriodo al montarse, antes de la primera carga.
      desde: null,
      hasta: null,
      cargando: true,
      noDisponible: false,
      pedidos: [],
      rango: null,
      busqueda: '',
      paginaActual: 1,
      porPagina: 20
    }
  },
  computed: {
    pedidosFiltrados () {
      const q = this.busqueda.trim().toLowerCase()
      if (!q) return this.pedidos
      return this.pedidos.filter(p => {
        const tipo = p.tipo_entrega === 'pickup' ? 'recojo' : 'delivery'
        const estado = p.cancelado ? 'cancelado' : (p.programado ? 'programado' : '')
        return [String(p.pedido_id), tipo, estado].some(v => v.toLowerCase().includes(q))
      })
    },
    pedidosPaginados () {
      const inicio = (this.paginaActual - 1) * this.porPagina
      return this.pedidosFiltrados.slice(inicio, inicio + this.porPagina)
    }
  },
  watch: {
    busqueda () {
      this.paginaActual = 1
    }
  },
  methods: {
    /* SelectorPeriodo emite tambien al montarse: esa es la primera carga. */
    alCambiarPeriodo (rango) {
      this.desde = rango.desde
      this.hasta = rango.hasta
      this.cargar()
    },
    fmtFecha (iso) {
      if (!iso) return ''
      const p = iso.split('-')
      return p[2] + '/' + p[1] + '/' + p[0]
    },
    fmtHora (ts) {
      if (!ts) return '—'
      // El tracking viene en ISO UTC; se muestra la hora local del navegador.
      const d = new Date(ts)
      return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
    },
    descargar () {
      descargarExcel('tiempos-preparacion', [{
        nombre: 'Tiempos de preparación',
        columnas: [
          { clave: 'pedido', titulo: 'Pedido', ancho: 12, tipo: 'numero' },
          { clave: 'fecha', titulo: 'Fecha', ancho: 12, tipo: 'texto' },
          { clave: 'inicio', titulo: 'Inicio preparación', ancho: 18, tipo: 'texto' },
          { clave: 'listo', titulo: 'Listo', ancho: 12, tipo: 'texto' },
          { clave: 'prep', titulo: 'Preparación (min)', ancho: 18, tipo: 'decimal' },
          { clave: 'tipo', titulo: 'Tipo', ancho: 12, tipo: 'texto' },
          { clave: 'estado', titulo: 'Estado', ancho: 14, tipo: 'texto' }
        ],
        filas: this.pedidosFiltrados.map(p => ({
          pedido: p.pedido_id,
          fecha: this.fmtFecha(p.fecha),
          inicio: this.fmtHora(p.inicio_preparacion),
          listo: this.fmtHora(p.listo),
          // null y no "N/A": asi la columna queda numerica y se puede promediar
          // en la planilla sin que el texto rompa la formula.
          prep: p.prep_min,
          tipo: p.tipo_entrega === 'pickup' ? 'Recojo' : 'Delivery',
          estado: p.cancelado ? 'Cancelado' : (p.programado ? 'Programado' : 'Normal')
        }))
      }])
    },
    cargar () {
      this.cargando = true
      this.noDisponible = false
      this.paginaActual = 1


      MetricasCocinaGateway.preparacion(this.desde, this.hasta)
        .then(payload => {
          if (payload.status === 'UNAVAILABLE') {
            this.noDisponible = true
            return
          }
          const r = payload.result || {}
          this.pedidos = r.pedidos || []
          this.rango = r.rango || null
        })
        .catch(() => { this.noDisponible = true })
        .finally(() => { this.cargando = false })
    }
  }
}
</script>

<style scoped>
.tiempos-prep { padding: 28px 34px; }
.tiempos-prep__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 26px;
}
.tiempos-prep__titulo { font-size: 28px; font-weight: 700; color: #1a202c; margin: 0; }
.tiempos-prep__rango { font-size: 15px; font-weight: 400; color: #6b7684; margin-left: 14px; }
.tiempos-prep__cargando { padding: 60px 0; text-align: center; color: #8a94a0; font-size: 14px; }
.tiempos-prep__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.tiempos-prep__buscar { flex: 1; min-width: 240px; max-width: 380px; margin-bottom: 0; }
.tiempos-prep__paginacion { margin-top: 18px; text-align: right; }
.celda-na { color: #c2c9d1; }
.pill {
  display: inline-block;
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
}
.pill--neutra { background: #f5f5f5; color: #737373; }
.pill--roja { background: #fef2f2; color: #dc2626; }

/*
  _element-tables.scss vuelve flex el ".cell" del header (para centrarlo
  verticalmente), sin justify-content. En un contenedor flex, text-align no
  hace nada — el titulo queda pegado a la izquierda aunque header-align sea
  "center". No se toca el scss global: podria afectar otras tablas de la app.
*/
::v-deep .el-table__header th.is-center .cell {
  justify-content: center;
}
</style>
